/**
 * Contacts: the people captured from conversations.
 *
 * The filter row mirrors the backend's named filters exactly — there is no
 * free-form query language, by design — and the CSV export sends the same
 * filters, so what downloads is what the table shows.
 *
 * The Status column is the working state of a contact, and it is styled rather
 * than badged: New is bold because it is the only row that needs attention,
 * Read is plain because it does not, and Lead Created is coloured because it is
 * the outcome the tab exists to produce. Opening a contact marks it Read, which
 * is what "read" means.
 */

import { useCallback, useEffect, useState } from 'react';

import { api } from '../api/client';
import type {
  Contact,
  ContactDetail,
  ContactFilters,
  ContactStatus,
  LeadRegion,
} from '../api/types';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Modal,
  PageHeader,
  Spinner,
  StatusBadge,
} from '../components/ui';
import { formatDateTime } from '../lib/format';
import { useBots } from '../hooks/useBots';

/**
 * How each status renders in the table.
 *
 * Deliberately not a `StatusBadge`: the three states are a progression an
 * operator scans down a column, and weight plus colour reads faster than three
 * pills of the same size.
 */
const STATUS_STYLES: Record<ContactStatus, string> = {
  new: 'font-semibold text-slate-900',
  read: 'font-normal text-slate-500',
  lead_created: 'font-medium text-emerald-700',
};

const STATUS_LABELS: Record<ContactStatus, string> = {
  new: 'New',
  read: 'Read',
  lead_created: 'Lead Created',
};

const REGION_LABELS: Record<LeadRegion, string> = {
  us: 'United States',
  us_territory: 'US territory',
  canada: 'Canada',
  international: 'International',
  unknown: 'No postal code',
};

export function Contacts(): JSX.Element {
  const { selected } = useBots();
  const slug = selected?.slug ?? null;

  const [items, setItems] = useState<Contact[]>([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState<ContactFilters>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewing, setViewing] = useState<ContactDetail | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      const result = await api.listContacts(slug, filters);
      setItems(result.items);
      setTotal(result.total);
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not load contacts');
    } finally {
      setLoading(false);
    }
  }, [slug, filters]);

  useEffect(() => {
    void load();
  }, [load]);

  function update<K extends keyof ContactFilters>(
    key: K,
    value: ContactFilters[K],
  ): void {
    setFilters((current) => {
      const next = { ...current };
      if (value === undefined || value === '') {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
  }

  async function handleView(contact: Contact): Promise<void> {
    if (!slug) return;
    // The GET marks a `new` contact as read, so the row is refreshed after.
    const detail = await api.getContact(slug, contact.id);
    setViewing(detail);
    if (contact.status === 'new') await load();
  }

  async function handleDelete(contact: Contact): Promise<void> {
    if (!slug) return;
    if (!window.confirm(`Delete ${contact.email ?? contact.name ?? 'this contact'}?`))
      return;
    await api.deleteContact(slug, contact.id);
    await load();
  }

  async function handleAddToNetsuite(contact: Contact): Promise<void> {
    if (!slug) return;

    if (
      !window.confirm(
        `Send ${contact.email ?? contact.name ?? 'this contact'} to Formaspace sales?\n\n` +
          'This creates a NetSuite lead, and the middleware assigns the sales rep ' +
          'from the zip code. The chat transcript and an English summary are included.',
      )
    )
      return;

    setBusyId(contact.id);
    setNotice(null);
    try {
      const result = await api.addContactToNetsuite(slug, contact.id);
      setNotice(
        result.dry_run
          ? `Marked as Lead Created. ${result.detail}`
          : `Sent to Formaspace sales${result.lead_id ? ` (lead ${result.lead_id})` : ''}. ` +
              'The middleware creates the NetSuite lead and assigns the rep.',
      );
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not create the lead');
    } finally {
      setBusyId(null);
    }
  }

  if (!slug) return <EmptyState title="No bot selected" />;

  const selectClass =
    'rounded-md border border-slate-300 px-2 py-1.5 text-sm outline-none focus:border-brand-blue';

  return (
    <div>
      <PageHeader
        title="Contacts"
        description={`${total} matching contact${total === 1 ? '' : 's'}`}
        actions={
          <Button
            variant="secondary"
            onClick={() => void api.downloadContactsCsv(slug, filters)}
          >
            Export CSV
          </Button>
        }
      />

      {error && <ErrorNotice message={error} />}
      {notice && (
        <div className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {notice}
        </div>
      )}

      <Card className="mb-4 flex flex-wrap items-center gap-2 p-3">
        <input
          value={filters.search ?? ''}
          onChange={(event) => update('search', event.target.value)}
          placeholder="Name, email, company, phone or zip…"
          className="min-w-56 flex-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm outline-none focus:border-brand-blue"
        />
        <select
          value={filters.status ?? ''}
          onChange={(event) =>
            update('status', (event.target.value || undefined) as ContactStatus)
          }
          className={selectClass}
        >
          <option value="">Any status</option>
          <option value="new">New</option>
          <option value="read">Read</option>
          <option value="lead_created">Lead Created</option>
        </select>
        <select
          value={filters.lead_region ?? ''}
          onChange={(event) =>
            update('lead_region', (event.target.value || undefined) as LeadRegion)
          }
          className={selectClass}
        >
          <option value="">Any territory</option>
          <option value="us">United States</option>
          <option value="us_territory">US territory</option>
          <option value="canada">Canada</option>
          <option value="international">International</option>
          <option value="unknown">No postal code</option>
        </select>
        <select
          value={filters.type ?? ''}
          onChange={(event) =>
            update('type', (event.target.value || undefined) as ContactFilters['type'])
          }
          className={selectClass}
        >
          <option value="">Any type</option>
          <option value="lead">Lead</option>
          <option value="customer">Customer</option>
        </select>
        <label className="flex items-center gap-1.5 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={filters.has_email === true}
            onChange={(event) => update('has_email', event.target.checked || undefined)}
          />
          Has email
        </label>
        <label className="flex items-center gap-1.5 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={filters.has_phone === true}
            onChange={(event) => update('has_phone', event.target.checked || undefined)}
          />
          Has phone
        </label>
      </Card>

      <Card>
        {loading ? (
          <Spinner />
        ) : items.length === 0 ? (
          <EmptyState
            title="No contacts yet"
            hint="Contacts appear here once the assistant captures them in a conversation."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium">Email</th>
                  <th className="px-4 py-2 font-medium">Company</th>
                  <th className="px-4 py-2 font-medium">Phone</th>
                  <th className="px-4 py-2 font-medium">Zip</th>
                  <th className="px-4 py-2 font-medium">Territory</th>
                  <th className="px-4 py-2 font-medium">Date Created</th>
                  <th className="px-4 py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((contact) => (
                  <tr key={contact.id} className="hover:bg-slate-50">
                    <td className={`px-4 py-2 ${STATUS_STYLES[contact.status]}`}>
                      {STATUS_LABELS[contact.status]}
                    </td>
                    <td className="px-4 py-2 font-medium text-slate-800">
                      {contact.name ?? '—'}
                    </td>
                    <td className="px-4 py-2 text-slate-600">{contact.email ?? '—'}</td>
                    <td className="px-4 py-2 text-slate-600">
                      {contact.company ?? '—'}
                    </td>
                    <td className="px-4 py-2 text-slate-600">{contact.phone ?? '—'}</td>
                    <td className="px-4 py-2 text-slate-600">
                      {contact.postal_code ?? '—'}
                    </td>
                    <td className="px-4 py-2">
                      <StatusBadge status={contact.lead_region} />
                    </td>
                    <td className="px-4 py-2 text-slate-600">
                      {formatDateTime(contact.first_seen)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2">
                      <button
                        type="button"
                        onClick={() => void handleView(contact)}
                        className="rounded px-2 py-1 text-xs text-slate-600 hover:bg-slate-100"
                      >
                        View
                      </button>
                      {sentToNetsuite(contact) ? (
                        contact.netsuite_url ? (
                          <a
                            href={contact.netsuite_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="rounded bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 hover:underline"
                            title={`${sentLabel(sentToNetsuite(contact))}. Opens a NetSuite search for this email.`}
                          >
                            ✓ In NetSuite ↗
                          </a>
                        ) : (
                          <span
                            className="rounded bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700"
                            title={sentLabel(sentToNetsuite(contact))}
                          >
                            ✓ In NetSuite
                          </span>
                        )
                      ) : (
                        <button
                          type="button"
                          disabled={busyId === contact.id}
                          onClick={() => void handleAddToNetsuite(contact)}
                          className="rounded px-2 py-1 text-xs text-brand-blue hover:bg-sky-50 disabled:text-slate-400"
                        >
                          {busyId === contact.id ? 'Sending…' : 'Add to NetSuite'}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => void handleDelete(contact)}
                        className="rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {viewing && <ContactDrawer detail={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

/** The contact detail drawer, including every conversation's transcript. */
/** What the API records on a contact once the middleware has accepted it. */
interface NetsuiteSend {
  sent_by?: string;
  sent_at?: string;
  lead_id?: string;
}

/**
 * The record of a real send, or null. A dry run created nothing in NetSuite,
 * so it does not count and the button stays, as on the server.
 */
function sentToNetsuite(contact: Contact): NetsuiteSend | null {
  const record = contact.attributes.formaspace_middleware;
  if (!record || typeof record !== 'object') return null;
  const send = record as NetsuiteSend & { dry_run?: boolean };
  return send.dry_run === false ? send : null;
}

function sentLabel(send: NetsuiteSend | null): string {
  if (!send) return '';
  return `Sent to NetSuite by ${send.sent_by ?? 'a dashboard user'} on ${formatDateTime(
    send.sent_at ?? null,
  )}`;
}

function ContactDrawer({
  detail,
  onClose,
}: {
  detail: ContactDetail;
  onClose: () => void;
}): JSX.Element {
  const fields: [string, string | null][] = [
    ['Email', detail.email],
    ['Phone', detail.phone],
    ['Company', detail.company],
    ['Zip / postal code', detail.postal_code],
    ['Country', detail.country],
    ['Territory', REGION_LABELS[detail.lead_region]],
    ['Type', detail.type],
    ['Status', STATUS_LABELS[detail.status]],
    ['Date Created', formatDateTime(detail.first_seen)],
  ];

  return (
    <Modal title={detail.name ?? detail.email ?? 'Contact'} onClose={onClose} wide>
      <dl className="grid grid-cols-2 gap-3 text-sm md:grid-cols-3">
        {fields.map(([label, value]) => (
          <div key={label}>
            <dt className="text-xs text-slate-400">{label}</dt>
            <dd className="text-slate-800">{value || '—'}</dd>
          </div>
        ))}
      </dl>

      {detail.territory && !detail.territory.assignable && (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {detail.territory.detail}
        </div>
      )}

      {Object.keys(detail.attributes).length > 0 && (
        <div className="mt-4">
          <h3 className="text-sm font-semibold text-slate-900">Attributes</h3>
          <pre className="mt-1 overflow-auto rounded bg-slate-50 p-2 text-xs">
            {JSON.stringify(detail.attributes, null, 2)}
          </pre>
        </div>
      )}

      <h3 className="mt-5 text-sm font-semibold text-slate-900">
        Conversations ({detail.conversations.length})
      </h3>

      {detail.conversations.length === 0 && (
        <p className="mt-1 text-sm text-slate-500">
          This contact is not linked to any conversation.
        </p>
      )}

      {detail.conversations.map((conversation) => (
        <div
          key={conversation.id}
          className="mt-3 rounded-md border border-slate-200 bg-white"
        >
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-3 py-2 text-xs text-slate-500">
            <span className="truncate">{conversation.page_url ?? 'Unknown page'}</span>
            <span className="whitespace-nowrap">
              {formatDateTime(conversation.started_at)} · {conversation.message_count}{' '}
              msgs
            </span>
          </div>
          <div className="max-h-72 space-y-2 overflow-y-auto p-3">
            {conversation.messages.length === 0 ? (
              <p className="text-sm text-slate-500">This conversation is empty.</p>
            ) : (
              conversation.messages.map((message) => (
                <div
                  key={message.id}
                  className={
                    message.role === 'visitor'
                      ? 'flex justify-end'
                      : 'flex justify-start'
                  }
                >
                  <div
                    className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                      message.role === 'visitor'
                        ? 'bg-brand-blue text-white'
                        : message.role === 'agent'
                          ? 'bg-amber-50 text-slate-800'
                          : 'bg-slate-100 text-slate-800'
                    }`}
                  >
                    <div
                      className={`mb-0.5 text-[10px] uppercase tracking-wide ${
                        message.role === 'visitor' ? 'text-white/70' : 'text-slate-400'
                      }`}
                    >
                      {message.role === 'visitor'
                        ? 'Visitor'
                        : message.role === 'agent'
                          ? (message.agent_name ?? 'Agent')
                          : 'Assistant'}
                    </div>
                    <p className="whitespace-pre-wrap">{message.content}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      ))}
    </Modal>
  );
}
